/** @jsx jsx */
import React, {
	useState,
	useCallback,
	useEffect,
	useMemo,
	memo,
	useRef,
	useImperativeHandle,
	type KeyboardEvent,
	type ClipboardEvent,
	type ElementRef,
	type PropsWithChildren,
} from 'react';
import { css, jsx } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, Flex, xcss, Pressable } from '@atlaskit/primitives';
import type { OptionsType, OptionType } from '@atlaskit/select';
import SimpleTag from '@atlaskit/tag/simple-tag';
import Tooltip from '@atlaskit/tooltip';
import type { BusinessSelectOption } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import LazySelect from '@atlassian/jira-business-fields/src/common/ui/lazy-select/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { TagListWrapper } from '@atlassian/jira-business-fields/src/common/ui/tag-list-wrapper/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { DEFAULT_ROW_HEIGHT, LIST_FIELD_POPUP_OFFSET } from '../../../common/constants.tsx';
import { CellContainer, CellContentWrapper } from '../../../common/ui/list-cell/styled.tsx';
import { type CellOptionType, useCellOptions } from '../../../common/ui/use-cell-options/index.tsx';
import messages from './messages.tsx';
import type { SingleSelectCellProps } from './types.tsx';

const transformValueWithOptionId = (values?: CellOptionType[] | CellOptionType | null) => {
	if (!values) {
		return [];
	}
	const valuesArray = Array.isArray(values) ? values : [values];
	return valuesArray.map(({ optionId, value }) => ({
		value,
		label: value,
		optionId,
	}));
};

const getValue = (values: BusinessSelectOption | BusinessSelectOption[] | null) => {
	if (Array.isArray(values)) {
		return values[0]?.value ?? '';
	}

	return values?.value ?? '';
};

const transformLabels = (values: OptionsType) => values.map(({ value }) => value).join(', ');

const POPUP_OFFSET: [number, number] = LIST_FIELD_POPUP_OFFSET;
const POPUP_OFFSET_NEW: [number, number] = [0, -DEFAULT_ROW_HEIGHT - 1];

const SingleSelectCell = memo<SingleSelectCellProps>(
	({
		issueKey,
		fieldValue = null,
		width,
		fieldKey,
		fieldName,
		isEditable,
		fieldType,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		issueType = '',
		issueTypeId = '',
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		isHovering: isHoveringNew = false,
		inputValue,
		onInputChange,
		cellRef,
	}: SingleSelectCellProps) => {
		const { latestFieldOptions, getLatestFieldValue } = useCellOptions({
			fieldId: fieldKey,
		});

		const latestFieldValue = getLatestFieldValue(fieldValue);
		const { formatMessage } = useIntl();
		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;

		const [selectValue, setSelectValue] = useState<BusinessSelectOption[]>(
			transformValueWithOptionId(latestFieldValue),
		);

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [prevFieldValue, setPrevFieldValue] = useState(fieldValue);
		const clearOnBlur = useRef(false);

		useEffect(() => {
			// to update value when cell data is updated elsewhere
			if (!isEqual(fieldValue, prevFieldValue)) {
				setSelectValue(transformValueWithOptionId(latestFieldValue));
				setPrevFieldValue(fieldValue);
			}
		}, [fieldValue, latestFieldValue, prevFieldValue]);

		useEffect(() => {
			// to update selectValue when field config options data is updated elsewhere
			const latestSelectValue = transformValueWithOptionId(getLatestFieldValue(selectValue));
			if (!isEqual(selectValue, latestSelectValue)) {
				setSelectValue(latestSelectValue);
			}
		}, [getLatestFieldValue, selectValue]);

		const [{ value: editValue, error }, { saveValue, resetError }] = useEditField<
			BusinessSelectOption[] | BusinessSelectOption | null,
			null
		>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: transformValueWithOptionId(fieldValue),
			onSuccess: (v) => setSelectValue(transformValueWithOptionId(v)),
			onFailure: () => setSelectValue(transformValueWithOptionId(editValue)),
		});

		useEffect(() => {
			if (
				getValue(selectValue) !== getValue(editValue) &&
				fg('jwm_list_new_copy_paste_behaviour')
			) {
				setSelectValue(transformValueWithOptionId(editValue));
			}
		}, [editValue, selectValue]);

		const saveAndClose = useCallback(() => {
			setEditMode(false);
			if (!fg('jira_list_keyboard_nav')) {
				saveValue(selectValue[0] ?? null, null, createAnalyticsEvent({}));
			}
		}, [setEditMode, saveValue, selectValue, createAnalyticsEvent]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleClick = useCallback(() => {
			if (isEditable && !editMode) {
				setEditMode(true);
			}
		}, [editMode, isEditable, setEditMode]);

		useEffect(() => {
			if (!fg('jira_list_keyboard_nav') && isSelected === false && editMode) {
				saveAndClose();
			}
		}, [editMode, isSelected, saveAndClose]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyDown = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (event.key === 'Escape') {
					event.preventDefault();
					wrapperRef?.current?.focus();
				} else if (!editMode) {
					setEditMode(true);
				}
			},
			[editMode, setEditMode, wrapperRef],
		);

		const handleSelectChange = useCallback(
			(newValue: OptionType | null) => {
				resetError();

				const selection = Array.isArray(newValue) ? newValue[0] : newValue;
				setSelectValue(selection ? [selection] : []);

				if (fg('jira_list_keyboard_nav')) {
					if (fg('jwm_list_new_copy_paste_behaviour')) {
						saveValue(selection, null, createAnalyticsEvent({}));
					}
					if (newValue != null) {
						if (!fg('jwm_list_new_copy_paste_behaviour')) {
							saveValue(selection, null, createAnalyticsEvent({}));
						}
						setEditMode(false);
						wrapperRef &&
							setTimeout(() => {
								wrapperRef?.current?.focus();
							});
						clearOnBlur.current = false;
					} else {
						clearOnBlur.current = true;
					}

					return;
				}

				saveValue(selection, null, createAnalyticsEvent({}));
				setEditMode(false);

				if (!fg('jira_list_keyboard_nav')) {
					wrapperRef &&
						setTimeout(() => {
							wrapperRef?.current?.focus();
						});
				}
			},
			[createAnalyticsEvent, resetError, saveValue, setEditMode, wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

		const handleCutCopy = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();
				if (selectValue) {
					event.clipboardData.setData('text/plain', transformLabels(selectValue));
				}

				const actionSubjectId = 'single-select';
				if (event.type === 'copy') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', actionSubjectId);
				} else if (event.type === 'cut') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', actionSubjectId);
					// clear cell content
					setSelectValue([]);
					saveValue(null, null, createAnalyticsEvent({}));
				}
			},
			[selectValue, saveValue, createAnalyticsEvent],
		);

		const handlePaste = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				const clipText = event.clipboardData.getData('text/plain');
				const actionSubjectId = 'single-select';
				const labels = clipText.split(',');
				const options = latestFieldOptions.filter((option) =>
					labels.find((label) => label.trim() === option.label),
				);
				if (options.length > 0) {
					setSelectValue(options);
					saveValue(options[0], null, createAnalyticsEvent({}));
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', actionSubjectId, {
						valid: true,
					});
				} else {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', actionSubjectId, {
						valid: false,
						error: 'Not a valid option',
					});
				}
			},
			[latestFieldOptions, saveValue, createAnalyticsEvent],
		);

		const staticContent = useMemo(
			() =>
				selectValue.map(({ value }) =>
					fg('jwm-list-view-responsive-select-field') ? (
						<Box
							as="span"
							key={value}
							xcss={
								isVisualRefreshEnabled() && fg('jira_nav4_beta_drop_1')
									? selectFieldStyles
									: selectFieldStylesOld
							}
						>
							{value}
						</Box>
					) : (
						<SimpleTag key={value} text={value} />
					),
				),
			[selectValue],
		);
		// we need to sort the options so that the selected value is at the top of the list
		// as the first option is hovered by default and will be selected if `Enter` is pressed
		// immediately after the select dropdown is shown
		const sortedSelectOptions = useMemo<BusinessSelectOption[]>(() => {
			if (!selectValue.length) {
				return latestFieldOptions;
			}
			const selectedOptionIndex = latestFieldOptions.findIndex(
				(option) => option.value === selectValue[0].value,
			);
			const sortedOptions = [...latestFieldOptions];
			if (selectedOptionIndex > -1) {
				sortedOptions.splice(selectedOptionIndex, 1);
			}
			sortedOptions.unshift(selectValue[0]);
			return sortedOptions;
		}, [latestFieldOptions, selectValue]);

		const popupContent = useCallback(
			() => (
				<CellContentWrapper
					// -1px border left from gridcell
					preferredWidth={width - 1}
					onKeyDown={fg('jira_list_keyboard_nav') ? noop : handleKeyDown}
					data-testid="business-list.ui.list-view.single-select-cell.cell-content-wrapper"
				>
					<LazySelect
						autoFocus
						menuIsOpen={editMode}
						placeholder={formatMessage(messages.placeholder, { fieldName })}
						isClearable
						options={sortedSelectOptions}
						isMulti={false}
						onChange={handleSelectChange}
						value={selectValue}
						isInvalid={!!error}
						tabSelectsValue={!!fg('jira_list_keyboard_nav')}
						inputValue={fg('jira_list_keyboard_nav') ? inputValue : undefined}
						onInputChange={fg('jira_list_keyboard_nav') ? onInputChange : undefined}
					/>
				</CellContentWrapper>
			),
			[
				width,
				handleKeyDown,
				editMode,
				formatMessage,
				fieldName,
				sortedSelectOptions,
				handleSelectChange,
				selectValue,
				error,
				inputValue,
				onInputChange,
			],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) =>
				fg('jira_list_checkbox_convert_to_pressable') ? (
					<Pressable
						{...triggerProps}
						xcss={[
							popupTriggerStyles,
							fg('single_select_popup_width_adjustment') && popupTriggerContainerStylesXCSS,
						]}
					>
						<TagListWrapper isEditable={isEditable}>
							{selectValue.length > 0 && (
								<Tooltip content={transformLabels(selectValue)} position="top">
									{staticContent}
								</Tooltip>
							)}
						</TagListWrapper>
					</Pressable>
				) : (
					<div
						{...triggerProps}
						css={fg('single_select_popup_width_adjustment') && popupTriggerContainerStyles}
					>
						<TagListWrapper isEditable={isEditable}>
							{selectValue.length > 0 && (
								<Tooltip content={transformLabels(selectValue)} position="top">
									{staticContent}
								</Tooltip>
							)}
						</TagListWrapper>
					</div>
				),
			[isEditable, selectValue, staticContent],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onEscapeKeyPress = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				setEditMode(false);
				wrapperRef?.current?.focus();
			},
			[setEditMode, wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyPress = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === 'Escape') {
					onEscapeKeyPress(e);
				} else {
					!editMode && onKeyDown?.(e);
				}
			},
			[editMode, onEscapeKeyPress, onKeyDown],
		);

		const handleKeydownNew = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				if (editMode) {
					if (selectValue[0] == null) {
						if (e.key === 'Escape') {
							clearOnBlur.current = false;
						}
						setSelectValue(transformValueWithOptionId(prevFieldValue));
					} else if (e.key === 'Delete') {
						saveValue(null, null, createAnalyticsEvent({}));
						setEditMode(false);
						setTimeout(() => {
							wrapperRef?.current?.focus();
						});
						clearOnBlur.current = false;
					}
				}
				return false;
			},
			[
				createAnalyticsEvent,
				editMode,
				prevFieldValue,
				saveValue,
				selectValue,
				setEditMode,
				wrapperRef,
			],
		);

		const handleBlur = useCallback(() => {
			if (clearOnBlur.current) {
				saveValue(null, null, createAnalyticsEvent({}));
				clearOnBlur.current = false;
			}
			return false;
		}, [createAnalyticsEvent, saveValue]);

		const cellOverrides = useMemo(
			() => () =>
				fg('jira_list_keyboard_nav')
					? {
							onKeyDown: handleKeydownNew,
							onBlur: handleBlur,
						}
					: {},
			[handleKeydownNew, handleBlur],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const popupComponent = useMemo(
			() => getPopupComponentWithHeight({ numOptions: latestFieldOptions.length }),
			[latestFieldOptions],
		);

		const cellContent = (
			<>
				{isHovering || editMode || isSelected ? (
					<Popup
						isOpen={editMode}
						placement="bottom-start"
						autoFocus={false}
						offset={fg('jira_list_keyboard_nav') ? POPUP_OFFSET_NEW : POPUP_OFFSET}
						onClose={saveAndClose}
						popupComponent={popupComponent}
						content={popupContent}
						trigger={popupTrigger}
					/>
				) : (
					<TagListWrapper>{staticContent}</TagListWrapper>
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable={isEditable}
					testId="business-list.ui.list-view.single-select-cell.cell-container"
					hasPadding={false}
					{...(!fg('jwm_list_new_copy_paste_behaviour')
						? {
								onCopy: handleCutCopy,
								onCut: handleCutCopy,
								onPaste: handlePaste,
							}
						: {})}
				>
					<ContentWrapper>{cellContent}</ContentWrapper>
				</CellWrapperNew>
			);
		}

		return (
			<CellContainer
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onCopy={handleCutCopy}
				onCut={handleCutCopy}
				onPaste={handlePaste}
				onBlur={onBlur}
				onFocus={onFocus}
				onKeyDown={handleKeyPress}
				ref={onWrapperRef}
				isFocused={isSelected}
				onClick={handleClick}
				tabIndex={0}
				data-testid="business-list.ui.list-view.single-select-cell.cell-container"
			>
				{cellContent}
			</CellContainer>
		);
	},
);

export default SingleSelectCell;

const ContentWrapper = ({ children }: PropsWithChildren) => (
	<Flex xcss={[wrapperStyles]}>{children}</Flex>
);

const wrapperStyles = xcss({
	width: '100%',
	height: '100%',
});

const selectFieldStylesOld = xcss({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	borderRadius: 'border.radius',
	margin: 'space.050',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	backgroundColor: 'color.background.neutral',
	color: 'color.text',
});

const selectFieldStyles = xcss({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	color: 'color.text',
	margin: 'space.050',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	backgroundColor: 'color.background.neutral.subtle',
	boxSizing: 'border-box',
	borderRadius: 'border.radius',
	border: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
});

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
	width: '100%',
});

const popupTriggerContainerStylesXCSS = xcss({
	width: '100%',
});

const popupTriggerContainerStyles = css({
	width: '100%',
});
